import type {
  AskedQuestion,
  DeskQuestions,
  TalkMessageRequest,
  TalkQuestion,
  Asset,
  AssetVisibility,
  UpdateAssetRequest,
  UploadAssetRequest,
  Resource,
  ResourceTextPage,
  UpdateResourceRequest,
  UploadResourceRequest,
  DeskSkill,
  CheckDoneCriterionRequest,
  Playbook,
  SavePlaybookRequest,
  WorkItemLink,
  WorkHistoryEntry,
  Program,
  ProgramSettings,
  WorkReference,
  WorkItemType,
  WorkItemTypeRequest,
  Action,
  DecideActionsRequest,
  DeskToolPermission,
  SetDeskToolPermissionRequest,
  Account,
  AccountBranding,
  UpdateAccountBrandingRequest,
  CreateWorkerImageRequest,
  UpdateWorkerImageRequest,
  WorkerImage,
  WorkerImageBuildLog,
  WorkerImages,
  Inbox,
  RuntimeLogin,
  Conversation,
  ConversationDetail,
  ConversationMessage,
  DeskThreadMessage,
  CreateConversationRequest,
  CreateSetupConversationRequest,
  CreateViewRequest,
  UpdateViewPromptRequest,
  ViewChatRequest,
  ViewPromptState,
  UpdateViewRequest,
  ViewData,
  ViewDetail,
  ViewPreview,
  ViewQueryResult,
  ViewActionRequest,
  ViewActionResult,
  RowWork,
  ViewSummary,
  ViewVersion,
  CreateDbTableRequest,
  DbColumn,
  DbColumnInput,
  DbQueryResult,
  DbTable,
  DbTableData,
  UpdateDbColumnRequest,
  WorkspaceDatabase,
  CreateDataApiKeyRequest,
  CreateDataApiKeyResponse,
  DataApiKey,
  UpdateDataApiKeyRequest,
  SetupDraft,
  ApiToken,
  CreateApiTokenRequest,
  CreateApiTokenResponse,
  McpAppsResponse,
  OAuthConsentDecision,
  OAuthConsentRequest,
  McpToolsResponse,
  ActivityEvent,
  Agent,
  AgentDetail,
  Approval,
  ApprovalStatus,
  Artifact,
  Connection,
  ConnectionDetail,
  CreateAgentRequest,
  CreateConnectionRequest,
  CreateRunSecretRequest,
  CreateDeskRequest,
  CreateRuntimeProfileRequest,
  CreateSkillRequest,
  CreateTriggerRequest,
  CreateWorkItemRequest,
  CreateWorkspaceRequest,
  DependencyRelation,
  DeskAgentRole,
  Desk,
  DeskDetail,
  DeskGoalRequest,
  DeskGuidelines,
  DeskLearning,
  TrainingState,
  DeskReport,
  DeskTrigger,
  Execution,
  WorkItemPlan,
  ImportSkillRequest,
  LoginRequest,
  LoginResponse,
  Message,
  PutSkillFileRequest,
  RegisterRequest,
  AccessRequest,
  ResolveApprovalRequest,
  ReadyWorkItemRequest,
  RuntimeProfile,
  ScopeChoicesResponse,
  SendMessageRequest,
  Skill,
  SkillDetail,
  SkillFileContent,
  SessionSummary,
  TranscriptRun,
  UpdateAgentRequest,
  UpdateConnectionRequest,
  UpdateRunSecretRequest,
  RunSecret,
  UpdateDeskRequest,
  DeskInstallPlan,
  DeskMovePlan,
  DeskMoveResult,
  MoveDeskRequest,
  DeskWorkItemStats,
  CloneDeskRequest,
  CloneDeskResult,
  DeskInstallResult,
  DeskStoreEntry,
  DeskStoreEntryDetail,
  DeskTemplate,
  InstallDeskRequest,
  SaveDeskToStoreRequest,
  SaveDesksToStoreRequest,
  UpdateMeRequest,
  UpdateRuntimeProfileRequest,
  UpdateSkillRequest,
  UpdateTriggerRequest,
  UpdateWorkItemRequest,
  UpdateWorkspaceRequest,
  UsageReport,
  User,
  Uuid,
  WorkItem,
  WorkItemDependency,
  WorkItemStatus,
  WorkItemDetail,
  Workspace,
  WorkspaceOverview,
  FrontDeskSettings,
  DictationSettings,
  LiveVickiCallResult,
  LiveVickiSession,
  LiveVickiSettings,
  SaveVickiGoalRequest,
  SaveVickiMemoryRequest,
  VickiGoal,
  VickiAction,
  VickiDriveState,
  VickiKnowledge,
  VickiMemory,
  AccountPeople,
  DeskGuest,
  DeskShareLink,
  GuestAccess,
  GuestChat,
  GuestChatDetail,
  GuestDesk,
  GuestDeskRef,
  GuestFields,
  GuestMessage,
  GuestView,
  JoinSharedDeskRequest,
  JoinSharedDeskResponse,
  SharedDeskInfo,
  UpdateDeskShareRequest,
} from '@deskworks/shared-types';

export * from '@deskworks/shared-types';

// ---------------------------------------------------------------- errors

/** Thrown for every non-2xx response (and for network failures, with status 0). */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

// ---------------------------------------------------------------- options

export interface DeskworksClientOptions {
  /** Base URL of the control plane, e.g. `''` (same origin) or `http://localhost:3000`. `/api` is appended. */
  baseUrl?: string;
  /** Returns the current user JWT, or null when logged out. */
  getToken?: () => string | null | undefined;
  /** Called on any 401 response (except the login call itself). */
  onUnauthorized?: () => void;
  /** Custom fetch implementation (defaults to global fetch). */
  fetch?: typeof fetch;
}

export interface WorkItemListQuery {
  /** Single status, comma-separated list, or `open`. */
  status?: string;
  deskId?: Uuid;
  agentId?: Uuid;
  parentId?: Uuid;
  q?: string;
  limit?: number;
}

export interface ActivityQuery {
  deskId?: Uuid;
  limit?: number;
}

export interface ArtifactDownload {
  blob: Blob;
  filename: string;
  mimeType: string;
}

type Query = Record<string, string | number | boolean | null | undefined>;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  /** Skip auth handling (public endpoints). */
  public?: boolean;
  signal?: AbortSignal;
}

function buildQuery(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

function messageFromBody(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'message' in body) {
    const m = (body as { message: unknown }).message;
    if (Array.isArray(m)) return m.map(String).join(', ');
    if (typeof m === 'string' && m) return m;
  }
  if (typeof body === 'string' && body.trim()) return body.trim().slice(0, 500);
  return fallback;
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

const enc = encodeURIComponent;

// ---------------------------------------------------------------- client

export function createDeskworksClient(options: DeskworksClientOptions = {}) {
  const baseUrl = (options.baseUrl ?? '').replace(/\/+$/, '');
  const doFetch: typeof fetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));

  async function raw(path: string, opts: RequestOptions = {}): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (!opts.public) {
      const token = options.getToken?.();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    let body: BodyInit | undefined;
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    let res: Response;
    try {
      res = await doFetch(`${baseUrl}/api${path}${buildQuery(opts.query)}`, {
        method: opts.method ?? 'GET',
        headers,
        body,
        signal: opts.signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      throw new ApiError(0, 'The server can’t be reached right now.', err);
    }
    if (!res.ok) {
      let parsed: unknown = undefined;
      const text = await res.text().catch(() => '');
      try {
        parsed = text ? JSON.parse(text) : undefined;
      } catch {
        parsed = text;
      }
      if (res.status === 401 && !opts.public) options.onUnauthorized?.();
      throw new ApiError(res.status, messageFromBody(parsed, res.statusText || `HTTP ${res.status}`), parsed);
    }
    return res;
  }

  async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const res = await raw(path, opts);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (!text) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  const get = <T>(path: string, query?: Query) => request<T>(path, { query });
  const post = <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ?? {} });
  const put = <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body ?? {} });
  const patch = <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body });
  const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

  return {
    request,


    /** Which installation this is (dev | prod | local), the running version, and whether /register is open. */
    health: () => request<{ ok: boolean; environment?: string; version?: string; signup?: boolean; googleOAuth?: boolean }>('/health', { public: true }),

    auth: {
      login: (body: LoginRequest) => request<LoginResponse>('/auth/login', { method: 'POST', body, public: true }),
      /** A fresh token for the caller's, remembered as before. */
      refresh: () => request<LoginResponse>('/auth/refresh', { method: 'POST' }),
      /** Self-registration; 404 when it is switched off, 409 when the email is taken, 429 when rate-limited. */
      register: (body: RegisterRequest) => request<LoginResponse>('/auth/register', { method: 'POST', body, public: true }),
      me: () => get<{ user: User; accounts: Account[]; guestDesks: GuestDeskRef[] }>('/auth/me'),
      /** The profile: name and language. */
      updateMe: (body: UpdateMeRequest) => patch<User>('/auth/me', body),
      /** "Request access" from the landing page; 503 when no Leetz key is configured, 429 when rate-limited. */
      requestAccess: (body: AccessRequest) => request<void>('/access-requests', { method: 'POST', body, public: true }),
    },

    accounts: {
      list: () => get<Account[]>('/accounts'),
      workspaces: (accountId: Uuid) => get<Workspace[]>(`/accounts/${enc(accountId)}/workspaces`),
      /** "Needs you": waiting Work Items (with the agent's question), pending approvals, failed Work Items. */
      inbox: (accountId: Uuid, deskId?: Uuid) => get<Inbox>(`/accounts/${enc(accountId)}/inbox${deskId ? `?deskId=${enc(deskId)}` : ''}`),
      /** Desks across all workspaces of the account the user can see (each with `workspaceName`). */
      desks: (accountId: Uuid) => get<Desk[]>(`/accounts/${enc(accountId)}/desks`),
      createWorkspace: (accountId: Uuid, body: CreateWorkspaceRequest) =>
        post<Workspace>(`/accounts/${enc(accountId)}/workspaces`, body),
      /** The account's colors and logo in the app; only what is given changes, null resets it (account admins). */
      updateBranding: (accountId: Uuid, body: UpdateAccountBrandingRequest) =>
        patch<AccountBranding | null>(`/accounts/${enc(accountId)}/branding`, body),
    },

    /** An account's own images for agent runs (docs/features/worker-images.md); changes by account admins. */
    workerImages: {
      list: (accountId: Uuid) => get<WorkerImages>(`/accounts/${enc(accountId)}/worker-images`),
      create: (accountId: Uuid, body: CreateWorkerImageRequest) => post<WorkerImage>(`/accounts/${enc(accountId)}/worker-images`, body),
      /** A changed addition builds it again. */
      update: (id: Uuid, body: UpdateWorkerImageRequest) => patch<WorkerImage>(`/worker-images/${enc(id)}`, body),
      rebuild: (id: Uuid) => post<WorkerImage>(`/worker-images/${enc(id)}/rebuild`),
      buildLog: (id: Uuid) => get<WorkerImageBuildLog>(`/worker-images/${enc(id)}/build-log`),
      remove: (id: Uuid) => del<{ ok: true }>(`/worker-images/${enc(id)}`),
    },

    /** A desk shared by link (docs/features/desk-sharing.md): the link, joining it, and the account's people. */
    sharing: {
      /** The desk's link; null until it is made. */
      get: (deskId: Uuid) => get<DeskShareLink | null>(`/desks/${enc(deskId)}/share`),
      /** Makes the link on the first call; only what is given changes. */
      update: (deskId: Uuid, body: UpdateDeskShareRequest) => put<DeskShareLink>(`/desks/${enc(deskId)}/share`, body),
      /** A new address: the old one stops working; guests already in stay. */
      renew: (deskId: Uuid) => post<DeskShareLink>(`/desks/${enc(deskId)}/share/renew`),
      /** What the link shows before joining; `signedIn` sends the session so it knows the caller. */
      info: (token: string, signedIn: boolean) => request<SharedDeskInfo>(`/shared/${enc(token)}`, { public: !signedIn }),
      join: (token: string, body: JoinSharedDeskRequest, signedIn: boolean) =>
        request<JoinSharedDeskResponse>(`/shared/${enc(token)}/join`, { method: 'POST', body, public: !signedIn }),
      /** Members and desk guests of the account (account admins). */
      people: (accountId: Uuid) => get<AccountPeople>(`/accounts/${enc(accountId)}/people`),
      setGuestFields: (accountId: Uuid, fields: GuestFields) => put<GuestFields>(`/accounts/${enc(accountId)}/guest-fields`, fields),
      updateGuest: (id: Uuid, body: { access: GuestAccess }) => patch<DeskGuest>(`/desk-guests/${enc(id)}`, body),
      removeGuest: (id: Uuid) => del<void>(`/desk-guests/${enc(id)}`),
    },

    /** The shared desk as its guest sees it, with the guest's token or session (docs/features/desk-sharing.md). */
    guest: {
      desk: (deskId: Uuid) => get<GuestDesk>(`/guest/desks/${enc(deskId)}`),
      view: (deskId: Uuid, viewId: Uuid) => get<GuestView>(`/guest/desks/${enc(deskId)}/views/${enc(viewId)}`),
      viewData: (deskId: Uuid, viewId: Uuid, params: Record<string, unknown> = {}) =>
        get<ViewData>(`/guest/desks/${enc(deskId)}/views/${enc(viewId)}/data`, Object.keys(params).length ? { params: JSON.stringify(params) } : {}),
      viewQuery: (deskId: Uuid, viewId: Uuid, name: string, params: Record<string, unknown> = {}) =>
        get<ViewQueryResult>(
          `/guest/desks/${enc(deskId)}/views/${enc(viewId)}/queries/${enc(name)}`,
          Object.keys(params).length ? { params: JSON.stringify(params) } : {},
        ),
      chats: (deskId: Uuid) => get<GuestChat[]>(`/guest/desks/${enc(deskId)}/chats`),
      chat: (deskId: Uuid, id: Uuid) => get<GuestChatDetail>(`/guest/desks/${enc(deskId)}/chats/${enc(id)}`),
      startChat: (deskId: Uuid, body: { message: string }) => post<GuestChatDetail>(`/guest/desks/${enc(deskId)}/chats`, body),
      send: (deskId: Uuid, id: Uuid, body: { content: string }) => post<GuestMessage>(`/guest/desks/${enc(deskId)}/chats/${enc(id)}/messages`, body),
      /** A guest who came by the form registers: their Deskworks sign-in, keeping their chats. */
      register: (deskId: Uuid, body: { email: string; password: string }) => post<LoginResponse>(`/guest/desks/${enc(deskId)}/register`, body),
    },

    workspaces: {
      get: (workspaceId: Uuid) => get<Workspace>(`/workspaces/${enc(workspaceId)}`),
      update: (workspaceId: Uuid, body: UpdateWorkspaceRequest) => patch<Workspace>(`/workspaces/${enc(workspaceId)}`, body),
      /** Deletes the workspace with its desks, agents, connections, Work Items and database. Account owners/admins. */
      delete: (workspaceId: Uuid) => del<{ ok: true }>(`/workspaces/${enc(workspaceId)}`),
      overview: (workspaceId: Uuid) => get<WorkspaceOverview>(`/workspaces/${enc(workspaceId)}/overview`),
      activity: (workspaceId: Uuid, query: ActivityQuery = {}) =>
        get<ActivityEvent[]>(`/workspaces/${enc(workspaceId)}/activity`, { ...query }),
      approvals: (workspaceId: Uuid, status?: ApprovalStatus) =>
        get<Approval[]>(`/workspaces/${enc(workspaceId)}/approvals`, { status }),
      /** AI usage (tokens, USD) of the workspace between `from` and `to` (ISO dates; default: last 30 days). */
      usage: (workspaceId: Uuid, query: { from?: string; to?: string } = {}) =>
        get<UsageReport>(`/workspaces/${enc(workspaceId)}/usage`, { ...query }),
    },

    apiTokens: {
      /** Admins see all tokens of the account, members only their own. */
      list: (accountId: Uuid) => get<ApiToken[]>(`/accounts/${enc(accountId)}/api-tokens`),
      /** The plain token is only in this response — it is never returned again. */
      create: (accountId: Uuid, body: CreateApiTokenRequest) =>
        post<CreateApiTokenResponse>(`/accounts/${enc(accountId)}/api-tokens`, body),
      revoke: (id: Uuid) => del<{ ok: true }>(`/api-tokens/${enc(id)}`),
    },

    /** Apps connected by OAuth (Claude, ChatGPT, …) and the consent page that connects them. */
    mcpApps: {
      /** Admins see every person's apps, members their own. */
      list: (accountId: Uuid) => get<McpAppsResponse>(`/accounts/${enc(accountId)}/mcp-apps`),
      disconnect: (id: Uuid) => del<{ ok: true }>(`/mcp-apps/${enc(id)}`),
      describeRequest: (request: string) => post<OAuthConsentRequest>('/oauth/consent/describe', { request }),
      /** Allow or deny; the browser goes on to `redirectUrl` (the app). */
      decide: (body: OAuthConsentDecision) => post<{ redirectUrl: string }>('/oauth/consent', body),
    },

    agents: {
      list: (workspaceId: Uuid) => get<Agent[]>(`/workspaces/${enc(workspaceId)}/agents`),
      create: (workspaceId: Uuid, body: CreateAgentRequest) => post<Agent>(`/workspaces/${enc(workspaceId)}/agents`, body),
      get: (id: Uuid) => get<AgentDetail>(`/agents/${enc(id)}`),
      update: (id: Uuid, body: UpdateAgentRequest) => patch<Agent>(`/agents/${enc(id)}`, body),
      /** 409 while the agent leads a desk, has open work items or a worker is running. */
      delete: (id: Uuid) => del<{ ok: true }>(`/agents/${enc(id)}`),
    },

    runtimeProfiles: {
      list: (workspaceId: Uuid) => get<RuntimeProfile[]>(`/workspaces/${enc(workspaceId)}/runtime-profiles`),
      create: (workspaceId: Uuid, body: CreateRuntimeProfileRequest) =>
        post<RuntimeProfile>(`/workspaces/${enc(workspaceId)}/runtime-profiles`, body),
      update: (id: Uuid, body: UpdateRuntimeProfileRequest) =>
        patch<RuntimeProfile>(`/runtime-profiles/${enc(id)}`, body),
      /** Deletes the runtime (and its stored key/login). 409 when agents still use it or a worker is running. */
      delete: (id: Uuid) => del<{ ok: true }>(`/runtime-profiles/${enc(id)}`),
      /** Starts a subscription login container for the profile (cancels a previous one). */
      startLogin: (id: Uuid) => post<RuntimeLogin>(`/runtime-profiles/${enc(id)}/login`),
      /** Subscription runtimes: a short CLI call that reports the plan limits (see `usageCheck` on the profile). */
      checkUsage: (id: Uuid) => post<RuntimeProfile['usageCheck']>(`/runtime-profiles/${enc(id)}/usage-check`, {}),
      /** Agents on "Automatic" stop skipping the runtime (its limit or refused login is forgotten). */
      makeAvailable: (id: Uuid) => post<RuntimeProfile>(`/runtime-profiles/${enc(id)}/available`, {}),
    },

    runtimeLogins: {
      /** Poll every ~2 s while the login is active. */
      get: (id: Uuid) => get<RuntimeLogin>(`/runtime-logins/${enc(id)}`),
      /** Claude only: the code shown in the browser after signing in. */
      submitCode: (id: Uuid, code: string) => post<RuntimeLogin>(`/runtime-logins/${enc(id)}/code`, { code }),
      cancel: (id: Uuid) => post<RuntimeLogin>(`/runtime-logins/${enc(id)}/cancel`),
    },

    skills: {
      /** Deskworks skills (read-only) and the workspace's own skills. */
      list: (workspaceId: Uuid) => get<Skill[]>(`/workspaces/${enc(workspaceId)}/skills`),
      get: (workspaceId: Uuid, id: Uuid) => get<SkillDetail>(`/workspaces/${enc(workspaceId)}/skills/${enc(id)}`),
      create: (workspaceId: Uuid, body: CreateSkillRequest) => post<SkillDetail>(`/workspaces/${enc(workspaceId)}/skills`, body),
      /** A .zip/.skill archive or a SKILL.md (base64). 409 when the name exists and `replace` is not set. */
      import: (workspaceId: Uuid, body: ImportSkillRequest) => post<SkillDetail>(`/workspaces/${enc(workspaceId)}/skills/import`, body),
      update: (workspaceId: Uuid, id: Uuid, body: UpdateSkillRequest) =>
        patch<SkillDetail>(`/workspaces/${enc(workspaceId)}/skills/${enc(id)}`, body),
      remove: (workspaceId: Uuid, id: Uuid) => del<{ ok: true }>(`/workspaces/${enc(workspaceId)}/skills/${enc(id)}`),
      /** Copies a Deskworks skill into the workspace; the copy replaces it there. */
      customize: (workspaceId: Uuid, id: Uuid) => post<SkillDetail>(`/workspaces/${enc(workspaceId)}/skills/${enc(id)}/customize`),
      file: (workspaceId: Uuid, id: Uuid, path: string) =>
        get<SkillFileContent>(`/workspaces/${enc(workspaceId)}/skills/${enc(id)}/file`, { path }),
      putFile: (workspaceId: Uuid, id: Uuid, body: PutSkillFileRequest) =>
        put<SkillDetail>(`/workspaces/${enc(workspaceId)}/skills/${enc(id)}/file`, body),
      deleteFile: (workspaceId: Uuid, id: Uuid, path: string) =>
        request<SkillDetail>(`/workspaces/${enc(workspaceId)}/skills/${enc(id)}/file`, { method: 'DELETE', query: { path } }),
      /** The skill as a zip (one folder named like the skill), importable into Claude and ChatGPT. */
      export: async (workspaceId: Uuid, id: Uuid): Promise<ArtifactDownload> => {
        const res = await raw(`/workspaces/${enc(workspaceId)}/skills/${enc(id)}/export`);
        const blob = await res.blob();
        return {
          blob,
          filename: filenameFromDisposition(res.headers.get('Content-Disposition')) ?? 'skill.zip',
          mimeType: res.headers.get('Content-Type') ?? 'application/zip',
        };
      },
      /** Replaces the skills enabled for an agent; returns them resolved for the agent's workspace. */
      setForAgent: (agentId: Uuid, skillIds: Uuid[]) => put<Skill[]>(`/agents/${enc(agentId)}/skills`, { skillIds }),
    },

    connections: {
      list: (workspaceId: Uuid) => get<Connection[]>(`/workspaces/${enc(workspaceId)}/connections`),
      create: (workspaceId: Uuid, body: CreateConnectionRequest) =>
        post<Connection>(`/workspaces/${enc(workspaceId)}/connections`, body),
      /** `workspaceId`: the workspace the person looks from (one it is shared with sees only its own desks). */
      get: (id: Uuid, workspaceId?: Uuid) => get<ConnectionDetail>(`/connections/${enc(id)}`, { workspaceId }),
      update: (id: Uuid, body: UpdateConnectionRequest) => patch<Connection>(`/connections/${enc(id)}`, body),
      healthCheck: (id: Uuid) => post<Connection>(`/connections/${enc(id)}/health-check`),
      scopeChoices: (id: Uuid) => get<ScopeChoicesResponse>(`/connections/${enc(id)}/scope-choices`),
      remove: (id: Uuid) => del<{ ok: true }>(`/connections/${enc(id)}`),
      /** OAuth sign-in of an `mcp_server` connection: send the person to `authorizationUrl` (null: already signed in). */
      oauthStart: (id: Uuid, origin: string) => post<{ authorizationUrl: string | null }>(`/connections/${enc(id)}/oauth/start`, { origin }),
      oauthDisconnect: (id: Uuid) => post<ConnectionDetail>(`/connections/${enc(id)}/oauth/disconnect`),
      /** Tools of an `mcp_server` connection (cached server-side; `refresh` reconnects). */
      mcpTools: (id: Uuid, refresh = false) =>
        get<McpToolsResponse>(`/connections/${enc(id)}/mcp-tools`, { refresh: refresh ? 1 : undefined }),
    },
    secrets: {
      list: (workspaceId: Uuid) => get<RunSecret[]>(`/workspaces/${enc(workspaceId)}/secrets`),
      create: (workspaceId: Uuid, body: CreateRunSecretRequest) => post<RunSecret>(`/workspaces/${enc(workspaceId)}/secrets`, body),
      /** `workspaceId`: the workspace the person works in (the answer lists its desks). */
      update: (id: Uuid, body: UpdateRunSecretRequest, workspaceId?: Uuid) =>
        patch<RunSecret>(`/secrets/${enc(id)}${workspaceId ? `?workspaceId=${enc(workspaceId)}` : ''}`, body),
      remove: (id: Uuid) => del<{ ok: true }>(`/secrets/${enc(id)}`),
    },

    /** Tool calls of agents and what a desk lets them do (docs/features/actions-and-permissions.md). */
    actions: {
      forWorkItem: (workItemId: Uuid) => get<Action[]>(`/work-items/${enc(workItemId)}/actions`),
      decide: (actionId: Uuid, body: DecideActionsRequest) => post<Action[]>(`/actions/${enc(actionId)}/decision`, body),
      /** All waiting actions of one run. */
      decideGroup: (groupId: string, body: DecideActionsRequest) => post<Action[]>(`/action-groups/${enc(groupId)}/decision`, body),
      permissions: (deskId: Uuid) => get<DeskToolPermission[]>(`/desks/${enc(deskId)}/permissions`),
      setPermission: (deskId: Uuid, body: SetDeskToolPermissionRequest) => put<DeskToolPermission | null>(`/desks/${enc(deskId)}/permissions`, body),
    },

    desks: {
      list: (workspaceId: Uuid) => get<Desk[]>(`/workspaces/${enc(workspaceId)}/desks`),
      create: (workspaceId: Uuid, body: CreateDeskRequest) =>
        post<DeskDetail>(`/workspaces/${enc(workspaceId)}/desks`, body),
      get: (id: Uuid) => get<DeskDetail>(`/desks/${enc(id)}`),
      /** How long its Work Items take, per type. */
      workItemStats: (id: Uuid) => get<DeskWorkItemStats>(`/desks/${enc(id)}/work-item-stats`),
      update: (id: Uuid, body: UpdateDeskRequest) => patch<DeskDetail>(`/desks/${enc(id)}`, body),
      /** Deletes the desk with its Work Items, conversations, triggers and history; running workers are stopped. */
      delete: (id: Uuid) => del<{ ok: true }>(`/desks/${enc(id)}`),
      trigger: (id: Uuid, reason?: string) => post<{ eventId: Uuid }>(`/desks/${enc(id)}/trigger`, { reason }),
      /** What the desk learned, when and why, newest first. */
      learnings: (id: Uuid) => get<DeskLearning[]>(`/desks/${enc(id)}/learnings`),
      /** "Let's go": starts the desk's training chat with its Lead (the active one when there is one). */
      startTraining: (id: Uuid, body: { workItemId?: Uuid } = {}) => post<ConversationDetail>(`/desks/${enc(id)}/training`, body),
      /** The training: its chat, the Lead's plan, how far the desk is and its autonomy KPI. */
      training: (id: Uuid) => get<TrainingState>(`/desks/${enc(id)}/training`),
      /** A person decides the Lead's proposal in the training chat (the next stage, God mode) with one click. */
      decideTrainingProposal: (id: Uuid, body: { messageId: Uuid; accept: boolean }) =>
        post<{ ok: true }>(`/desks/${enc(id)}/training/proposal`, body),
      /** The Lead Agent's full session for its most recent desk evaluations, oldest first. */
      transcript: (id: Uuid) => get<TranscriptRun[]>(`/desks/${enc(id)}/transcript`),
      /** The desk's threads in one feed (the latest 200 messages, oldest first): the Lead and its agents talking. */
      thread: (id: Uuid) => get<DeskThreadMessage[]>(`/desks/${enc(id)}/thread`),
      pause: (id: Uuid) => post<DeskDetail>(`/desks/${enc(id)}/pause`),
      resume: (id: Uuid) => post<DeskDetail>(`/desks/${enc(id)}/resume`),
      /** Approves every Work Item of the Desk that waits for approval to start. */
      approveStarts: (id: Uuid) => post<{ approved: number }>(`/desks/${enc(id)}/approve-starts`),
      /** The desk's guidelines (written by people). */
      putGuidelines: (id: Uuid, content: string) => put<DeskGuidelines>(`/desks/${enc(id)}/guidelines`, { content }),
      /** Deprecated name of putGuidelines. */
      putPlaybook: (id: Uuid, content: string) => put<DeskGuidelines>(`/desks/${enc(id)}/guidelines`, { content }),
      /** Every version of one of the desk's playbooks, newest first. */
      playbookVersions: (id: Uuid, name: string) => get<Playbook[]>(`/desks/${enc(id)}/playbooks/${enc(name)}/versions`),
      /** A new playbook, or a new version of one, written by a person. */
      savePlaybook: (id: Uuid, body: SavePlaybookRequest) => post<Playbook>(`/desks/${enc(id)}/playbooks`, body),
      deletePlaybook: (id: Uuid, name: string) => del<{ ok: true }>(`/desks/${enc(id)}/playbooks/${enc(name)}`),
      skills: (id: Uuid) => get<DeskSkill[]>(`/desks/${enc(id)}/skills`),
      skillVersions: (id: Uuid, skillId: Uuid) =>
        get<{ version: number; description: string; body: string; changeNote: string; createdByAgentName: string | null; createdAt: string }[]>(
          `/desks/${enc(id)}/skills/${enc(skillId)}/versions`,
        ),
      workItemTypes: (id: Uuid) => get<WorkItemType[]>(`/desks/${enc(id)}/work-item-types`),
      createWorkItemType: (id: Uuid, body: WorkItemTypeRequest & { name: string }) => post<WorkItemType>(`/desks/${enc(id)}/work-item-types`, body),
      updateWorkItemType: (typeId: Uuid, body: WorkItemTypeRequest) =>
        patch<WorkItemType>(`/work-item-types/${enc(typeId)}`, body),
      deleteWorkItemType: (typeId: Uuid) => del<{ ok: true }>(`/work-item-types/${enc(typeId)}`),
      setAgent: (id: Uuid, agentId: Uuid, body: { role: DeskAgentRole; enabled?: boolean }) =>
        put<DeskDetail>(`/desks/${enc(id)}/agents/${enc(agentId)}`, body),
      removeAgent: (id: Uuid, agentId: Uuid) => del<DeskDetail>(`/desks/${enc(id)}/agents/${enc(agentId)}`),
      addConnection: (id: Uuid, connectionId: Uuid, permissions?: string[]) =>
        put<DeskDetail>(`/desks/${enc(id)}/connections/${enc(connectionId)}`, { permissions }),
      removeConnection: (id: Uuid, connectionId: Uuid) =>
        del<DeskDetail>(`/desks/${enc(id)}/connections/${enc(connectionId)}`),
      /** Gives a secret to the desk's runs: every agent (agentIds null) or only these. */
      putSecret: (id: Uuid, secretId: Uuid, agentIds: Uuid[] | null) => put<DeskDetail>(`/desks/${enc(id)}/secrets/${enc(secretId)}`, { agentIds }),
      removeSecret: (id: Uuid, secretId: Uuid) => del<DeskDetail>(`/desks/${enc(id)}/secrets/${enc(secretId)}`),
      addGoal: (id: Uuid, body: DeskGoalRequest) => post<DeskDetail>(`/desks/${enc(id)}/goals`, body),
      updateGoal: (id: Uuid, goalId: Uuid, body: Partial<DeskGoalRequest>) =>
        patch<DeskDetail>(`/desks/${enc(id)}/goals/${enc(goalId)}`, body),
      removeGoal: (id: Uuid, goalId: Uuid) => del<DeskDetail>(`/desks/${enc(id)}/goals/${enc(goalId)}`),
      /** The desk as a Desk Store template (what "Save to Desk Store" would keep). */
      template: (id: Uuid) => get<DeskTemplate>(`/desks/${enc(id)}/template`),
      /** Saves the desk into its account's Desk Store; 409 when the name exists and `replace` is not set. */
      saveToStore: (id: Uuid, body: SaveDeskToStoreRequest) => post<DeskStoreEntryDetail>(`/desks/${enc(id)}/desk-store`, body),
      /** What moving the desk into another workspace of its account would do; changes nothing. */
      movePlan: (id: Uuid, workspaceId: Uuid) => get<DeskMovePlan>(`/desks/${enc(id)}/move-plan`, { workspaceId }),
      /** Moves the desk with everything on it; 409 while something blocks it (running work, open hand-offs …). */
      move: (id: Uuid, body: MoveDeskRequest) => post<DeskMoveResult>(`/desks/${enc(id)}/move`, body),
      /** A copy of the desk in its workspace, adapted by its Lead to the prompt. */
      clone: (id: Uuid, body: CloneDeskRequest) => post<CloneDeskResult>(`/desks/${enc(id)}/clone`, body),
      /** A metric's target (docs/features/desk-reports.md); null removes it. */
      setMetricTarget: (id: Uuid, metricId: Uuid, body: { target: number | null; targetBy?: string | null }) =>
        patch<DeskDetail>(`/desks/${enc(id)}/metrics/${enc(metricId)}`, body),
      /** Starts a report now (docs/features/desk-reports.md); 409 while the Lead still writes the last one. */
      report: (id: Uuid) => post<DeskReport>(`/desks/${enc(id)}/reports`),
    },

    /** The desks' reports (docs/features/desk-reports.md), newest first. */
    reports: {
      list: (workspaceId: Uuid, query: { deskId?: Uuid } = {}) => get<DeskReport[]>(`/workspaces/${enc(workspaceId)}/reports`, { ...query }),
      /** "Done – improve": the discussion ends; the Lead improves its desk when the desk says so. */
      close: (workItemId: Uuid) => post<DeskReport>(`/work-items/${enc(workItemId)}/report/close`),
    },

    /** Ready-made desks (docs/features/desk-store.md): Deskworks's and the account's own. */
    deskStore: {
      list: (workspaceId: Uuid) => get<DeskStoreEntry[]>(`/workspaces/${enc(workspaceId)}/desk-store`),
      get: (workspaceId: Uuid, id: Uuid) => get<DeskStoreEntryDetail>(`/workspaces/${enc(workspaceId)}/desk-store/${enc(id)}`),
      /** What installing would do in this workspace; changes nothing. */
      plan: (workspaceId: Uuid, id: Uuid) => get<DeskInstallPlan>(`/workspaces/${enc(workspaceId)}/desk-store/${enc(id)}/plan`),
      /** Saves desks of the workspace (default: all) as one entry; 409 when the name exists and `replace` is not set. */
      saveDesks: (workspaceId: Uuid, body: SaveDesksToStoreRequest) => post<DeskStoreEntryDetail>(`/workspaces/${enc(workspaceId)}/desk-store`, body),
      install: (workspaceId: Uuid, id: Uuid, body: InstallDeskRequest) =>
        post<DeskInstallResult>(`/workspaces/${enc(workspaceId)}/desk-store/${enc(id)}/install`, body),
      remove: (workspaceId: Uuid, id: Uuid) => del<{ ok: true }>(`/workspaces/${enc(workspaceId)}/desk-store/${enc(id)}`),
    },

    sessions: {
      /** Every agent run of a workspace (optionally one Desk), newest first, without the steps. */
      list: (workspaceId: Uuid, query: { deskId?: Uuid; limit?: number } = {}) =>
        get<SessionSummary[]>(`/workspaces/${enc(workspaceId)}/sessions`, { ...query }),
      /** One run with its full session, step by step. */
      get: (executionId: Uuid) => get<TranscriptRun>(`/executions/${enc(executionId)}/transcript`),
    },

    conversations: {
      /** Active conversations of a desk, newest first. */
      list: (deskId: Uuid) => get<Conversation[]>(`/desks/${enc(deskId)}/conversations`),
      /** The agent must be on the desk; with `message` it starts right away. */
      create: (deskId: Uuid, body: CreateConversationRequest) =>
        post<ConversationDetail>(`/desks/${enc(deskId)}/conversations`, body),
      /** Messages oldest first, plus the latest execution. */
      get: (id: Uuid) => get<ConversationDetail>(`/conversations/${enc(id)}`),
      /** Sends a message and wakes the agent; `answers`: the agent's message whose offered answer it is. */
      send: (id: Uuid, content: string, resourceIds?: Uuid[], answers?: Uuid) =>
        post<ConversationMessage>(`/conversations/${enc(id)}/messages`, {
          content,
          ...(resourceIds?.length ? { resourceIds } : {}),
          ...(answers ? { answers } : {}),
        }),
      /** The agent's full session for each run that answered this chat. */
      transcript: (id: Uuid) => get<TranscriptRun[]>(`/conversations/${enc(id)}/transcript`),
      /** `writes`: the agent may use writing tools in this chat (docs/features/conversations.md#writing-in-a-chat). */
      update: (id: Uuid, body: { title?: string; status?: 'active' | 'archived'; writes?: boolean }) =>
        patch<Conversation>(`/conversations/${enc(id)}`, body),
      /** Calls the agent asked for in this chat while it may write. */
      actions: (id: Uuid) => get<Action[]>(`/conversations/${enc(id)}/actions`),
      /**
       * A message that asks desks with @Desk (docs/features/desk-talk.md); in a chat between desks, a desk or an agent of
       * it. Their Lead answers in the chat between the two sides; the answer shows here.
       */
      talk: (id: Uuid, body: TalkMessageRequest) =>
        post<{ message?: ConversationMessage; questions: TalkQuestion[] }>(`/conversations/${enc(id)}/talk`, body),
    },

    /** Desks talk to each other (docs/features/desk-talk.md). */
    talk: {
      /** A desk's questions: asked of it and by it (open), and the latest answered. */
      questions: (deskId: Uuid) => get<DeskQuestions>(`/desks/${enc(deskId)}/questions`),
      /** VICKI's chats with the desks of the account. */
      vicki: (accountId: Uuid) => get<Conversation[]>(`/accounts/${enc(accountId)}/front-desk/talks`),
      /** The questions asked from a chat (VICKI's, a person's @Desk), newest first, live: who answers, whom they ask. */
      ofChat: (conversationId: Uuid) => get<AskedQuestion[]>(`/conversations/${enc(conversationId)}/questions`),
    },

    /** Views: visualizations of the workspace database (docs/features/views.md). */
    views: {
      list: (workspaceId: Uuid, query: { deskId?: Uuid } = {}) => get<ViewSummary[]>(`/workspaces/${enc(workspaceId)}/views`, { ...query }),
      get: (id: Uuid) => get<ViewDetail>(`/views/${enc(id)}`),
      create: (workspaceId: Uuid, body: CreateViewRequest) => post<ViewDetail>(`/workspaces/${enc(workspaceId)}/views`, body),
      /** `code` creates a new live version. */
      update: (id: Uuid, body: UpdateViewRequest) => patch<ViewDetail>(`/views/${enc(id)}`, body),
      delete: (id: Uuid) => del<{ ok: true }>(`/views/${enc(id)}`),
      /** Results of the live version's queries. */
      data: (id: Uuid, params: Record<string, unknown> = {}) =>
        get<ViewData>(`/views/${enc(id)}/data`, Object.keys(params).length ? { params: JSON.stringify(params) } : {}),
      /** Runs unsaved code once with real data. */
      preview: (workspaceId: Uuid, code: string, params?: Record<string, unknown>) =>
        post<ViewPreview>(`/workspaces/${enc(workspaceId)}/views/preview`, { code, params }),
      /** One query of the live version with its own params (useQuery in a view). */
      query: (id: Uuid, name: string, params: Record<string, unknown> = {}) =>
        get<ViewQueryResult>(`/views/${enc(id)}/queries/${enc(name)}`, Object.keys(params).length ? { params: JSON.stringify(params) } : {}),
      /** One query of unsaved code (useQuery while trying code). */
      previewQuery: (workspaceId: Uuid, code: string, name: string, params?: Record<string, unknown>) =>
        post<ViewQueryResult>(`/workspaces/${enc(workspaceId)}/views/preview-query`, { code, name, params }),
      /** Runs an action of the live version on a row (a button in the view). */
      runAction: (id: Uuid, name: string, body: ViewActionRequest) => post<ViewActionResult>(`/views/${enc(id)}/actions/${enc(name)}`, body),
      /** Work on rows of a table: the newest linked Work Item per key and what waits for approval (useWork in a view). */
      rowWork: (workspaceId: Uuid, table: string, keys: string[]) =>
        get<Record<string, RowWork>>(`/workspaces/${enc(workspaceId)}/row-work`, { table, keys: JSON.stringify(keys) }),
      version: (id: Uuid, number: number) => get<ViewVersion>(`/views/${enc(id)}/versions/${number}`),
      accept: (id: Uuid, number: number) => post<ViewDetail>(`/views/${enc(id)}/versions/${number}/accept`, {}),
      reject: (id: Uuid, number: number) => post<ViewDetail>(`/views/${enc(id)}/versions/${number}/reject`, {}),
      restore: (id: Uuid, number: number) => post<ViewDetail>(`/views/${enc(id)}/versions/${number}/restore`, {}),
      /** The view's prompt and the view assistant's chat. */
      prompt: (id: Uuid) => get<ViewPromptState>(`/views/${enc(id)}/prompt`),
      /** "Update view": saves the prompt; the view assistant rewrites the view to match. */
      updatePrompt: (id: Uuid, body: UpdateViewPromptRequest) => post<ViewPromptState>(`/views/${enc(id)}/prompt`, body),
      /** A message to the view assistant in the chat next to the view (the chat starts on the first one). */
      chat: (id: Uuid, body: ViewChatRequest) => post<ConversationDetail>(`/views/${enc(id)}/chat`, body),
    },

    /** The workspace's own database: viewer and structure changes (docs/features/workspace-database.md). */
    database: {
      get: (workspaceId: Uuid) => get<WorkspaceDatabase>(`/workspaces/${enc(workspaceId)}/database`),
      tables: (workspaceId: Uuid) => get<DbTable[]>(`/workspaces/${enc(workspaceId)}/database/tables`),
      table: (workspaceId: Uuid, schema: string, table: string, query: { offset?: number; limit?: number } = {}) =>
        get<DbTableData>(`/workspaces/${enc(workspaceId)}/database/tables/${enc(schema)}/${enc(table)}`, { ...query }),
      createTable: (workspaceId: Uuid, body: CreateDbTableRequest) => post<DbTableData>(`/workspaces/${enc(workspaceId)}/database/tables`, body),
      dropTable: (workspaceId: Uuid, schema: string, table: string) =>
        del<{ ok: true }>(`/workspaces/${enc(workspaceId)}/database/tables/${enc(schema)}/${enc(table)}`),
      addColumn: (workspaceId: Uuid, schema: string, table: string, body: DbColumnInput) =>
        post<DbColumn[]>(`/workspaces/${enc(workspaceId)}/database/tables/${enc(schema)}/${enc(table)}/columns`, body),
      updateColumn: (workspaceId: Uuid, schema: string, table: string, column: string, body: UpdateDbColumnRequest) =>
        patch<DbColumn[]>(`/workspaces/${enc(workspaceId)}/database/tables/${enc(schema)}/${enc(table)}/columns/${enc(column)}`, body),
      dropColumn: (workspaceId: Uuid, schema: string, table: string, column: string) =>
        del<DbColumn[]>(`/workspaces/${enc(workspaceId)}/database/tables/${enc(schema)}/${enc(table)}/columns/${enc(column)}`),
      /** One statement; `read` runs read-only, `write` needs write access. */
      query: (workspaceId: Uuid, sql: string, mode: 'read' | 'write' = 'read') =>
        post<DbQueryResult>(`/workspaces/${enc(workspaceId)}/database/query`, { sql, mode }),
    },

    /** Keys for websites and apps that use the workspace's tables over /api/data (docs/features/data-api.md). */
    dataApi: {
      keys: (workspaceId: Uuid) => get<DataApiKey[]>(`/workspaces/${enc(workspaceId)}/data-api/keys`),
      /** The plain key is only in this response — it is never returned again. */
      createKey: (workspaceId: Uuid, body: CreateDataApiKeyRequest) =>
        post<CreateDataApiKeyResponse>(`/workspaces/${enc(workspaceId)}/data-api/keys`, body),
      updateKey: (id: Uuid, body: UpdateDataApiKeyRequest) => patch<DataApiKey>(`/data-api/keys/${enc(id)}`, body),
      revokeKey: (id: Uuid) => del<{ ok: true }>(`/data-api/keys/${enc(id)}`),
    },

    /** Start page: setup chats and their drafts (docs/features/setup-assistant.md). */
    setup: {
      conversations: (workspaceId: Uuid) => get<Conversation[]>(`/workspaces/${enc(workspaceId)}/setup-conversations`),
      /** Setup chats of every workspace of the account the person may read. */
      accountConversations: (accountId: Uuid) => get<Conversation[]>(`/accounts/${enc(accountId)}/setup-conversations`),
      start: (workspaceId: Uuid, body: CreateSetupConversationRequest) =>
        post<ConversationDetail>(`/workspaces/${enc(workspaceId)}/setup-conversations`, body),
      /** The chat's open draft, else its last one. */
      draft: (conversationId: Uuid) => get<{ draft: SetupDraft | null }>(`/conversations/${enc(conversationId)}/setup-draft`),
      toggle: (draftId: Uuid, number: number, enabled: boolean) =>
        patch<SetupDraft>(`/setup-drafts/${enc(draftId)}/changes/${number}`, { enabled }),
      /** Applies all switched-on changes at once; nothing changes when one is refused. */
      apply: (draftId: Uuid) => post<SetupDraft>(`/setup-drafts/${enc(draftId)}/apply`, {}),
      discard: (draftId: Uuid) => post<SetupDraft>(`/setup-drafts/${enc(draftId)}/discard`, {}),
    },

    /** Dictation in the message fields: the runtime that transcribes recordings (docs/features/conversations.md#dictation). */
    dictation: {
      settings: (accountId: Uuid) => get<DictationSettings>(`/accounts/${enc(accountId)}/dictation`),
      setRuntime: (accountId: Uuid, runtimeProfileId: Uuid | null) => patch<DictationSettings>(`/accounts/${enc(accountId)}/dictation`, { runtimeProfileId }),
      transcribe: (accountId: Uuid, body: { audioBase64: string; mimeType: string; language?: 'en' | 'de' }) =>
        post<{ text: string }>(`/accounts/${enc(accountId)}/dictation/transcriptions`, body),
    },

    /** The Front Desk: chats with VICKI, her mode, her approval cards and what she knows (docs/features/front-desk.md). */
    frontDesk: {
      /** Every chat with VICKI in the account. */
      conversations: (accountId: Uuid) => get<Conversation[]>(`/accounts/${enc(accountId)}/front-desk/conversations`),
      /** Starts a chat with VICKI; it lives in the account's own workspace (its model) and works on the whole account. */
      start: (workspaceId: Uuid, body: { message: string; runtimeProfileId: Uuid; title?: string }) =>
        post<ConversationDetail>(`/workspaces/${enc(workspaceId)}/front-desk/conversations`, body),
      /** The runtimes a chat with VICKI may run on: every active one of the account. */
      runtimes: (accountId: Uuid) => get<RuntimeProfile[]>(`/accounts/${enc(accountId)}/front-desk/runtimes`),
      /** VICKI's tasks: open ones and the last finished (docs/features/front-desk.md#tasks). */
      tasks: (accountId: Uuid) => get<WorkItem[]>(`/accounts/${enc(accountId)}/front-desk/tasks`),
      /** Gives VICKI a task; she starts on it right away. */
      addTask: (accountId: Uuid, body: { title: string; description?: string }) =>
        post<{ id: Uuid; workspaceId: Uuid; title: string; status: WorkItemStatus }>(`/accounts/${enc(accountId)}/front-desk/tasks`, body),
      settings: (accountId: Uuid) => get<FrontDeskSettings>(`/accounts/${enc(accountId)}/front-desk/settings`),
      setSettings: (accountId: Uuid, body: Partial<FrontDeskSettings>) => patch<FrontDeskSettings>(`/accounts/${enc(accountId)}/front-desk/settings`, body),
      /** The approval cards of a chat. */
      actions: (conversationId: Uuid) => get<VickiAction[]>(`/conversations/${enc(conversationId)}/vicki-actions`),
      /** The approval cards of one of her tasks. */
      taskActions: (workItemId: Uuid) => get<VickiAction[]>(`/work-items/${enc(workItemId)}/vicki-actions`),
      decide: (actionId: Uuid, decision: 'approve' | 'reject', comment?: string) =>
        post<VickiAction>(`/vicki-actions/${enc(actionId)}/decide`, { decision, ...(comment ? { comment } : {}) }),
      knowledge: (accountId: Uuid) => get<VickiKnowledge>(`/accounts/${enc(accountId)}/front-desk/knowledge`),
      memory: (accountId: Uuid, id: Uuid) => get<VickiMemory>(`/accounts/${enc(accountId)}/front-desk/memories/${enc(id)}`),
      addMemory: (accountId: Uuid, body: SaveVickiMemoryRequest) => post<VickiMemory>(`/accounts/${enc(accountId)}/front-desk/memories`, body),
      updateMemory: (accountId: Uuid, id: Uuid, body: SaveVickiMemoryRequest) => put<VickiMemory>(`/accounts/${enc(accountId)}/front-desk/memories/${enc(id)}`, body),
      revokeMemory: (accountId: Uuid, id: Uuid) => post<VickiMemory>(`/accounts/${enc(accountId)}/front-desk/memories/${enc(id)}/revoke`, {}),
      deleteMemory: (accountId: Uuid, id: Uuid) => del<void>(`/accounts/${enc(accountId)}/front-desk/memories/${enc(id)}`),
      discardNotes: (accountId: Uuid, noteIds: Uuid[]) => post<{ ok: true }>(`/accounts/${enc(accountId)}/front-desk/notes/discard`, { noteIds }),
      /** VICKI's goals; only owners and admins change them. */
      goals: (accountId: Uuid) => get<VickiGoal[]>(`/accounts/${enc(accountId)}/front-desk/goals`),
      addGoal: (accountId: Uuid, body: SaveVickiGoalRequest) => post<VickiGoal>(`/accounts/${enc(accountId)}/front-desk/goals`, body),
      updateGoal: (accountId: Uuid, id: Uuid, body: SaveVickiGoalRequest) => put<VickiGoal>(`/accounts/${enc(accountId)}/front-desk/goals/${enc(id)}`, body),
      deleteGoal: (accountId: Uuid, id: Uuid) => del<void>(`/accounts/${enc(accountId)}/front-desk/goals/${enc(id)}`),
      /** What a chat is for and what VICKI watches for it (docs/features/front-desk.md#driving-a-chat). */
      drive: (conversationId: Uuid) => get<VickiDriveState>(`/conversations/${enc(conversationId)}/vicki-drive`),
      /** A person ends a mission: VICKI stops driving it. */
      stopMission: (missionId: Uuid) => post<VickiDriveState>(`/vicki-missions/${enc(missionId)}/stop`, {}),
      endWatch: (watchId: Uuid) => post<VickiDriveState>(`/vicki-watches/${enc(watchId)}/end`, {}),
      /** Live VICKI: the OpenAI API runtime that pays for talking with her (docs/features/front-desk.md#live-vicki). */
      live: (accountId: Uuid) => get<LiveVickiSettings>(`/accounts/${enc(accountId)}/front-desk/live`),
      setLive: (accountId: Uuid, runtimeProfileId: Uuid | null) => patch<LiveVickiSettings>(`/accounts/${enc(accountId)}/front-desk/live`, { runtimeProfileId }),
      /** A voice session in a chat: a short-lived key for the browser's connection to OpenAI. */
      startLive: (conversationId: Uuid, language?: 'en' | 'de') => post<LiveVickiSession>(`/conversations/${enc(conversationId)}/live`, language ? { language } : {}),
      /** One of VICKI's tools, called by the voice session. */
      liveCall: (conversationId: Uuid, name: string, args: Record<string, unknown>) =>
        post<LiveVickiCallResult>(`/conversations/${enc(conversationId)}/live/calls`, { name, arguments: args }),
    },

    /** Files of a workspace (every desk reads them) or of one desk (docs/features/resources.md). */
    resources: {
      /** deskId: one desk's; 'workspace': the workspace's own; conversationId: added in that chat. */
      list: (workspaceId: Uuid, query: { deskId?: Uuid | 'workspace'; conversationId?: Uuid } = {}) =>
        get<Resource[]>(`/workspaces/${enc(workspaceId)}/resources`, { ...query }),
      upload: (workspaceId: Uuid, body: UploadResourceRequest) => post<Resource>(`/workspaces/${enc(workspaceId)}/resources`, body),
      text: (id: Uuid, offset = 0) => get<ResourceTextPage>(`/resources/${enc(id)}/text`, { offset }),
      update: (id: Uuid, body: UpdateResourceRequest) => patch<Resource>(`/resources/${enc(id)}`, body),
      remove: (id: Uuid) => del<{ ok: true }>(`/resources/${enc(id)}`),
      download: async (id: Uuid, fallbackName = 'download'): Promise<ArtifactDownload> => {
        const res = await raw(`/resources/${enc(id)}/download`);
        const blob = await res.blob();
        return {
          blob,
          filename: filenameFromDisposition(res.headers.get('Content-Disposition')) ?? fallbackName,
          mimeType: res.headers.get('Content-Type') ?? blob.type ?? 'application/octet-stream',
        };
      },
    },

    triggers: {
      create: (deskId: Uuid, body: CreateTriggerRequest) => post<DeskTrigger>(`/desks/${enc(deskId)}/triggers`, body),
      update: (id: Uuid, body: UpdateTriggerRequest) => patch<DeskTrigger>(`/triggers/${enc(id)}`, body),
      delete: (id: Uuid) => del<{ ok: true }>(`/triggers/${enc(id)}`),
    },

    workItems: {
      list: (workspaceId: Uuid, query: WorkItemListQuery = {}) =>
        get<WorkItem[]>(`/workspaces/${enc(workspaceId)}/work-items`, { ...query }),
      create: (body: CreateWorkItemRequest) => post<WorkItemDetail>('/work-items', body),
      get: (id: Uuid) => get<WorkItemDetail>(`/work-items/${enc(id)}`),
      update: (id: Uuid, body: UpdateWorkItemRequest) => patch<WorkItemDetail>(`/work-items/${enc(id)}`, body),
      activity: (id: Uuid, afterSeq?: number) => get<ActivityEvent[]>(`/work-items/${enc(id)}/activity`, { afterSeq }),
      messages: (id: Uuid) => get<Message[]>(`/work-items/${enc(id)}/messages`),
      transcript: (id: Uuid) => get<TranscriptRun[]>(`/work-items/${enc(id)}/transcript`),
      sendMessage: (id: Uuid, body: SendMessageRequest) => post<Message>(`/work-items/${enc(id)}/messages`, body),
      pause: (id: Uuid) => post<WorkItemDetail>(`/work-items/${enc(id)}/pause`),
      resume: (id: Uuid) => post<WorkItemDetail>(`/work-items/${enc(id)}/resume`),
      cancel: (id: Uuid) => post<WorkItemDetail>(`/work-items/${enc(id)}/cancel`),
      retry: (id: Uuid) => post<WorkItemDetail>(`/work-items/${enc(id)}/retry`),
      ready: (id: Uuid, body: ReadyWorkItemRequest = {}) => post<WorkItemDetail>(`/work-items/${enc(id)}/ready`, body),
      approve: (id: Uuid, body: ResolveApprovalRequest = {}) =>
        post<WorkItemDetail>(`/work-items/${enc(id)}/approve`, body),
      reject: (id: Uuid, body: ResolveApprovalRequest = {}) =>
        post<WorkItemDetail>(`/work-items/${enc(id)}/reject`, body),
      addDependency: (id: Uuid, body: { dependsOnWorkItemId: Uuid; relationType: DependencyRelation }) =>
        post<WorkItemDependency[]>(`/work-items/${enc(id)}/dependencies`, body),
      removeDependency: (dependencyId: Uuid) => del<{ ok: true }>(`/dependencies/${enc(dependencyId)}`),
      executions: (id: Uuid) => get<Execution[]>(`/work-items/${enc(id)}/executions`),
      plans: (id: Uuid) => get<WorkItemPlan[]>(`/work-items/${enc(id)}/plans`),
      artifacts: (id: Uuid) => get<Artifact[]>(`/work-items/${enc(id)}/artifacts`),
      links: (id: Uuid) => get<WorkItemLink[]>(`/work-items/${enc(id)}/links`),
      /** What everyone did on the item; a person adds what they did (docs/features/work-items.md#work-history). */
      history: (id: Uuid) => get<WorkHistoryEntry[]>(`/work-items/${enc(id)}/history`),
      addHistory: (id: Uuid, body: { summary: string; refs?: WorkReference[] }) => post<WorkHistoryEntry>(`/work-items/${enc(id)}/history`, body),
      // Programs and waking again (docs/features/programs.md).
      program: (id: Uuid) => get<Program | null>(`/work-items/${enc(id)}/program`),
      saveProgram: (id: Uuid, body: Partial<ProgramSettings>) => put<Program>(`/work-items/${enc(id)}/program`, body),
      stopWake: (id: Uuid) => del<WorkItem>(`/work-items/${enc(id)}/wake`),
      /** Definition of done: check a point off or open it again, add one for this item, remove one the item added. */
      checkDone: (id: Uuid, point: number, body: CheckDoneCriterionRequest) => post<WorkItem>(`/work-items/${enc(id)}/done-criteria/${point}`, body),
      addDone: (id: Uuid, text: string) => post<WorkItem>(`/work-items/${enc(id)}/done-criteria`, { text }),
      removeDone: (id: Uuid, point: number) => del<WorkItem>(`/work-items/${enc(id)}/done-criteria/${point}`),
      link: (id: Uuid, body: { table: string; rowKey: string; note?: string }) => post<WorkItemLink>(`/work-items/${enc(id)}/links`, body),
      unlink: (linkId: Uuid) => del<{ ok: true }>(`/work-item-links/${enc(linkId)}`),
    },

    approvals: {
      list: (workspaceId: Uuid, status?: ApprovalStatus) =>
        get<Approval[]>(`/workspaces/${enc(workspaceId)}/approvals`, { status }),
      approve: (id: Uuid, body: ResolveApprovalRequest = {}) => post<Approval>(`/approvals/${enc(id)}/approve`, body),
      reject: (id: Uuid, body: ResolveApprovalRequest = {}) => post<Approval>(`/approvals/${enc(id)}/reject`, body),
    },

    executions: {
      get: (id: Uuid) => get<Execution>(`/executions/${enc(id)}`),
      logs: (id: Uuid) => get<{ logs: string; source: string }>(`/executions/${enc(id)}/logs`),
      /** Stops the agent's step so it takes the person's newest message in now (a CLI runtime). */
      interrupt: (id: Uuid) => post<{ ok: true }>(`/executions/${enc(id)}/interrupt`),
    },

    /** Every stored file, private or public (docs/features/assets.md). */
    assets: {
      list: (
        workspaceId: Uuid,
        query: { deskId?: Uuid | 'workspace'; conversationId?: Uuid; workItemId?: Uuid; visibility?: AssetVisibility; filename?: string; limit?: number } = {},
      ) => get<Asset[]>(`/workspaces/${enc(workspaceId)}/assets`, { ...query }),
      get: (id: Uuid) => get<Asset>(`/assets/${enc(id)}`),
      upload: (workspaceId: Uuid, body: UploadAssetRequest) => post<Asset>(`/workspaces/${enc(workspaceId)}/assets`, body),
      update: (id: Uuid, body: UpdateAssetRequest) => patch<Asset>(`/assets/${enc(id)}`, body),
      remove: (id: Uuid) => del<{ ok: true }>(`/assets/${enc(id)}`),
      /** The file with the Authorization header, as a Blob (for previews and downloads). */
      content: async (id: Uuid, fallbackName = 'download'): Promise<ArtifactDownload> => {
        const res = await raw(`/assets/${enc(id)}/content`);
        const blob = await res.blob();
        return {
          blob,
          filename: filenameFromDisposition(res.headers.get('Content-Disposition')) ?? fallbackName,
          mimeType: res.headers.get('Content-Type') ?? blob.type ?? 'application/octet-stream',
        };
      },
    },

    artifacts: {
      /** Path of the download endpoint (requires the Authorization header — use `download`). */
      downloadPath: (id: Uuid) => `${baseUrl}/api/artifacts/${enc(id)}/download`,
      /** Fetches the artifact with the Authorization header and returns it as a Blob. */
      download: async (id: Uuid, fallbackName = 'download'): Promise<ArtifactDownload> => {
        const res = await raw(`/artifacts/${enc(id)}/download`);
        const blob = await res.blob();
        return {
          blob,
          filename: filenameFromDisposition(res.headers.get('Content-Disposition')) ?? fallbackName,
          mimeType: res.headers.get('Content-Type') ?? blob.type ?? 'application/octet-stream',
        };
      },
    },
  };
}

export type DeskworksClient = ReturnType<typeof createDeskworksClient>;
